import {
  createRootRoute,
  HeadContent,
  Outlet,
  Scripts,
  redirect,
  useRouterState,
} from "@tanstack/react-router";

import { getCurrentUserFn, type CurrentUser } from "@/server/auth.functions";
import { currentPortal, isInvitePath, partnerPage, partnerPublicPath } from "@/auth/current-portal";
import type { Portal } from "@/auth/portal";
import { PartnerShell } from "@/braining/components/partner-shell";
import { Toaster } from "@/components/ui/sonner";
import { AppShell } from "@/braining/components/app-shell";
import { EstimatesProvider } from "@/braining/store";

import appCss from "../styles.css?url";

// Без описания намеренно: в превью только название. Если убрать один
// og:description, Telegram подставит обычный description — поэтому нет обоих.
const SITE_TITLE = "Портал Braining Group";

/**
 * Абсолютный адрес для превью: относительный og:image мессенджеры понимают
 * через раз. Теги нужны ботам, а боты видят только серверную отрисовку —
 * там домен берётся из PUBLIC_URL (у стенда и прода он свой). В браузере
 * хватает текущего origin. Без PUBLIC_URL остаётся относительный путь —
 * хуже, но не ломается.
 */
function absoluteUrl(path: string): string {
  const base = import.meta.env.SSR
    ? (process.env.PUBLIC_URL ?? "").trim().replace(/\/+$/, "")
    : window.location.origin;
  return `${base}${path}`;
}

// `/privacy` открыт снаружи намеренно: ссылку на него требует Google при
// публикации приложения OAuth, и она должна открываться без входа.
// /join — приглашение сотрудника по ссылке (0035): до входа, без панели.
const PUBLIC_PATHS = new Set<string>(["/login", "/privacy", "/join"]);

export interface RootContext {
  user: CurrentUser | null;
  portal: Portal;
}

/**
 * Корень приложения: вход обязателен везде, кроме страницы входа.
 *
 * Расчёт по-прежнему считается в браузере ядром `src/calc/` — на сервер
 * уехало только хранение. Так экраны остаются быстрыми, а пересчёт не
 * зависит от сети.
 */
export const Route = createRootRoute({
  beforeLoad: async ({ location }): Promise<RootContext> => {
    const path = location.pathname;
    const portal = currentPortal();

    // Поддомен партнёров: кабинет, вход и приглашение — и больше ничего.
    // Экранов сотрудника здесь нет даже пустыми: адрес уводит в кабинет.
    if (portal === "partners") {
      if (partnerPublicPath(path)) return { user: null, portal };
      const user = await getCurrentUserFn();
      if (!user) throw redirect({ to: "/login" });
      if (!partnerPage(path)) throw redirect({ to: "/cabinet" });
      return { user, portal };
    }

    // Кабинет и приглашения живут только на поддомене партнёров.
    if (partnerPage(path) || isInvitePath(path)) throw redirect({ to: "/" });
    if (PUBLIC_PATHS.has(path)) return { user: null, portal };
    const user = await getCurrentUserFn();
    if (!user) throw redirect({ to: "/login" });
    return { user, portal };
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: SITE_TITLE },
      { name: "robots", content: "noindex, nofollow" },
      // Превью ссылки в мессенджерах. Корень уводит на /login, и у той свой
      // <title> — поэтому название для превью задаётся отдельно, og:title
      // мессенджеры берут раньше заголовка страницы.
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Braining Group" },
      { property: "og:locale", content: "ru_RU" },
      { property: "og:title", content: SITE_TITLE },
      { property: "og:image", content: absoluteUrl("/og.png?v=2") },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: SITE_TITLE },
      { name: "twitter:image", content: absoluteUrl("/og.png?v=2") },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Иконки — набор с braining.group как есть: портал агентства должен
      // выглядеть во вкладке так же, как их сайт.
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48x48.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="ru">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { user, portal } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Страница входа живёт без панели и без стора: ей нечего показывать и
  // некому принадлежать.
  const bare = portal === "partners" ? partnerPublicPath(pathname) : PUBLIC_PATHS.has(pathname);
  if (bare) {
    return (
      <>
        <Outlet />
        <Toaster richColors position="top-right" />
      </>
    );
  }

  // Кабинет партнёра — своя оболочка и без стора смет: стор зовёт функции
  // сотрудника, а подрядчику они отвечают 403.
  if (portal === "partners") {
    return (
      <PartnerShell user={user}>
        <Outlet />
        <Toaster richColors position="top-right" />
      </PartnerShell>
    );
  }

  // Ключ по пользователю обязателен. Без него при смене аккаунта провайдер
  // не перемонтируется, его загрузка отрабатывает один раз — и новый
  // пользователь видит калькуляторы предыдущего. В ядре на этом уже
  // обжигались, см. src/routes/__root.tsx.
  return (
    <EstimatesProvider key={user?.id ?? "anon"}>
      <AppShell user={user}>
        <Outlet />
        <Toaster richColors position="top-right" />
      </AppShell>
    </EstimatesProvider>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-6xl font-semibold text-muted-foreground">404</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Такой страницы нет. Возможно, экран ещё не собран.
        </p>
      </div>
    </div>
  );
}
